import { BaseFormData } from '../components/types';

// Common validation functions that can be reused across forms
export const baseFormValidations = {
  // Generic single selection validation factory
  createSingleSelectionValidation: (fieldName: string) => (data: BaseFormData) => !!data[fieldName],
  
  // Form-specific validations moved to individual form configs
  
  // Step 3: Business information
  abnLookupStep: (data: BaseFormData) => {
    // For autocomplete: need businessSearch
    // For manual entry: need businessName, selectedABN, postcode, state with proper validation
    if (data.dataSource === 'autocomplete') {
      return !!data.businessSearch;
    } else if (data.dataSource === 'manual') {
      // Validate business name: required, minimum 2 characters
      const businessNameValid = !!(data.businessName && typeof data.businessName === 'string' && data.businessName.trim().length >= 2);
      
      // Validate ABN: required, exactly 11 digits, numeric only (after removing spaces)
      const cleanedABN = data.selectedABN && typeof data.selectedABN === 'string' ? data.selectedABN.replace(/\s/g, '') : '';
      const abnValid = !!(cleanedABN && /^\d{11}$/.test(cleanedABN));
      
      // Validate postcode: required, exactly 4 digits, numeric only
      // const postcodeValid = !!(data.postcode && typeof data.postcode === 'string' && /^\d{4}$/.test(data.postcode));
      
      // // Validate state: required
      // const stateValid = !!data.state;
      
      // return businessNameValid && abnValid && postcodeValid && stateValid;
      // If ABN exists, it must be valid; if ABN doesn't exist, business name must be valid
      if (data.selectedABN) {
        return businessNameValid && abnValid;
      } else {
        return businessNameValid;
      }
    } else {
      if (data.businessSearch && data.selectedABN) {
        return true;
      }
    }
    return false; // Need to choose a data source
  },
  
  // Step 4: Business Details & Metrics
  businessDetailsStep: (data: BaseFormData) => {
    // Validate required fields for business details and metrics
    const industryValid = data.industry === "Other" 
      ? !!(data.customIndustry && typeof data.customIndustry === 'string' && data.customIndustry.trim().length > 0)
      : !!(data.industry && data.industry !== 'Select');
    
    return !!(
      data.countryOfRegistration && 
      data.state && 
      data.businessType &&
      industryValid &&
      data.numberOfEmployees && data.numberOfEmployees !== 'Select' &&
      data.annualTurnover && data.annualTurnover !== 'Select'
    );
  },
  
  // Step 5: Business Description (optional)
  textFieldStep: () => true,
  
  // Step 6: Contact Details
  contactDetailsStep: (data: BaseFormData) => {
    // Validate all required contact fields
    const firstNameValid = !!(data.firstName && typeof data.firstName === 'string' && data.firstName.trim().length >= 2);
    const lastNameValid = !!(data.lastName && typeof data.lastName === 'string' && data.lastName.trim().length >= 2);
    const workEmailValid = !!(data.workEmail && typeof data.workEmail === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.workEmail));
    const phoneValid = !!(data.phone && typeof data.phone === 'string' && data.phone.trim().length >= 8);
    const termsAcceptedValid = !!data.termsAccepted;
    
    // Conditional validation for referredBy field
    const shouldRequireReferredBy = data.howDidYouHear === "Business referral (e.g. accountant, client, partner)" ||
                                   data.howDidYouHear === "Financial adviser or broker";
    
    const referredByValid = shouldRequireReferredBy 
      ? !!(data.referredBy && typeof data.referredBy === 'string' && data.referredBy.trim().length >= 1)
      : true; // Not required if condition not met
    
    return firstNameValid && lastNameValid && workEmailValid && phoneValid && termsAcceptedValid && referredByValid;
  },
};

// Helper function to create form validations with overrides
export function createFormValidations<T extends Record<string, unknown>>(overrides: T = {} as T) {
  return {
    ...baseFormValidations,
    ...overrides,
  };
}

// Type for form validations
export type FormValidations = ReturnType<typeof createFormValidations>;
